import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Loader2, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { useMyDrafts } from "../../hooks/postQueries";
import { backgroundOptions } from "../../utils/postConstants";
import { deleteDrafts } from "../../services/draftService";
import DraftReviewsModal from "./DraftReviewsModal";
import ErrorModal from "./ErrorModal";
import FetchErrorState from "../Common/FetchErrorState";

const MAX_SELECTABLE_DRAFTS = 20;

const getBackgroundClass = (backgroundKey) => {
  const bgOption = backgroundOptions[backgroundKey];
  return bgOption ? bgOption.class : backgroundOptions.white.class;
};

const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) callback();
      },
      { threshold: 0.1, rootMargin: "100px", ...options }
    );

    observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [callback, options]);

  return targetRef;
};

const DraftGridItem = React.memo(({ draft, onSelect, isSelectMode, isChecked, onToggleCheck }) => {
  const textColor = draft.styling?.color === "white" ? "text-white" : "text-black";
  const bgClass = getBackgroundClass(draft.styling?.background || "white");
  const fontClass = draft.styling?.font || "font-georgia";

  const handleClick = () => {
    if (isSelectMode) {
      onToggleCheck(draft._id);
    } else {
      onSelect(draft._id);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`aspect-square rounded-md relative overflow-hidden ${bgClass} cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg`}
    >
      {isSelectMode && (
        <div className="absolute top-2 right-2 z-10">
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
              isChecked
                ? "bg-red-600 border-red-600"
                : "bg-white/80 border-gray-400"
            }`}
          >
            {isChecked && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
        </div>
      )}

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <h3 className={`text-center font-bold text-lg md:text-xl ${textColor} ${fontClass} line-clamp-3`}>
          {draft.title || "Untitled"}
        </h3>
      </div>
    </div>
  );
});

const DraftModal = ({ isOpen, onClose, onSelectDraft }) => {
  const { apiCall } = useAuth();
  const queryClient = useQueryClient();

  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectionError, setSelectionError] = useState("");
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const {
    data: draftsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useMyDrafts();

  const allDrafts = useMemo(() => {
    if (!draftsData) return [];
    return draftsData.pages.flatMap((page) => page.posts || []);
  }, [draftsData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const handleDraftClick = (draftId) => {
    if (onSelectDraft) {
      onSelectDraft(draftId);
    } else {
      setSelectedDraftId(draftId);
    }
  };

  const handleToggleCheck = useCallback((draftId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(draftId)) {
        next.delete(draftId);
        setSelectionError("");
      } else {
        if (next.size >= MAX_SELECTABLE_DRAFTS) {
          setSelectionError(`You can only select up to ${MAX_SELECTABLE_DRAFTS} drafts at a time`);
          return prev;
        }
        next.add(draftId);
        setSelectionError("");
      }
      return next;
    });
  }, []);

  const handleToggleSelectMode = () => {
    setIsSelectMode((prev) => !prev);
    setCheckedIds(new Set());
    setSelectionError("");
  };

  const handleBulkDelete = async () => {
    if (checkedIds.size === 0) return;
    if (checkedIds.size > MAX_SELECTABLE_DRAFTS) {
      setSelectionError(`You can only delete up to ${MAX_SELECTABLE_DRAFTS} drafts at a time`);
      return;
    }

    setSelectionError("");
    setIsDeleting(true);
    const result = await deleteDrafts(apiCall, Array.from(checkedIds));
    setIsDeleting(false);

    if (!result.success) {
      setErrorModal({
        isOpen: true,
        message: result.error || "Failed to delete drafts. Please try again.",
      });
      return;
    }

    setCheckedIds(new Set());
    setIsSelectMode(false);
    queryClient.invalidateQueries({ queryKey: ["myDrafts"] });
  };

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  // Lock background scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Reset select mode whenever the modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsSelectMode(false);
      setCheckedIds(new Set());
      setSelectionError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-lg max-h-[85vh] flex flex-col"
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-black">
            {onSelectDraft ? "My Drafts" : "Draft Reviews"}
          </h3>

          {onSelectDraft && allDrafts.length > 0 && (
            <button
              onClick={handleToggleSelectMode}
              className="text-sm font-semibold text-black hover:text-gray-600 transition-colors"
            >
              {isSelectMode ? "Cancel" : "Select"}
            </button>
          )}
        </div>

        {/* Modal Content */}
        <div className="px-4 py-4 overflow-y-auto flex-1">
          {isLoading && allDrafts.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : error && allDrafts.length === 0 ? (
            <FetchErrorState
              message="We couldn't load your drafts."
              onRetry={() => refetch()}
            />
          ) : allDrafts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-full border border-gray-300 bg-white flex items-center justify-center mb-3">
                <FileText className="w-6 h-6 text-gray-900" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">
                {onSelectDraft ? "No saved drafts yet" : "No drafts to show"}
              </p>
              <p className="text-xs text-gray-500 max-w-xs">
                Your saved drafts will show up here
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {allDrafts.map((draft) => (
                  <DraftGridItem
                    key={draft._id}
                    draft={draft}
                    onSelect={handleDraftClick}
                    isSelectMode={isSelectMode}
                    isChecked={checkedIds.has(draft._id)}
                    onToggleCheck={handleToggleCheck}
                  />
                ))}
              </div>

              {hasNextPage && (
                <div ref={loadMoreRef} className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Bulk delete bar */}
        {isSelectMode && checkedIds.size > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
            {selectionError && (
              <p className="text-xs text-amber-600 font-medium mb-2 text-center">
                {selectionError}
              </p>
            )}
            <button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDeleting
                ? "Deleting..."
                // : `Delete ${checkedIds.size} draft${checkedIds.size > 1 ? "s" : ""}`}
                : "Delete"}
            </button>
          </div>
        )}
      </div>

      <DraftReviewsModal
        isOpen={!!selectedDraftId}
        onClose={(e) => {
          if (e?.stopPropagation) e.stopPropagation();
          setSelectedDraftId(null);
        }}
        draftId={selectedDraftId}
      />
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default DraftModal;